import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { gerarManualBatch, listManualRequisicoesCompra, type ListManualRcsQuery } from './api';
import { parseColabNotaFiscalCodigo } from './types';
import type { ManualRequisicaoCompra, NotaFiscalColabPendenteItem, NotaMaeOption } from './types';

function formatBRL(v: number): string {
  return v.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

/** Linha do grid com estado editável de valor + emissaoOK. */
interface RcRow extends ManualRequisicaoCompra {
  rowId: string;
  valor: number;
  emissaoOK: boolean;
}

export interface ManualDialogProps {
  open: boolean;
  companyId: number;
  linhaSelecionada: NotaFiscalColabPendenteItem | null;
  modelo: string;
  serie: string;
  notaMae: NotaMaeOption | null;
  onClose: () => void;
  onEmitido: () => void;
}

export function ManualDialog(props: ManualDialogProps): JSX.Element {
  const { open, companyId, linhaSelecionada, modelo, serie, notaMae, onClose, onEmitido } = props;
  const { t } = useTranslation();
  const toast = useToast();

  // Estado por linha (chave = rowId). Reset a cada open via key no Dialog é
  // possível mas simplifica: reset quando query.data muda (nova NF Colab).
  const [rowState, setRowState] = useState<Record<string, { valor: number; emissaoOK: boolean }>>(
    {},
  );

  const listQuery: ListManualRcsQuery | null = useMemo(() => {
    if (!linhaSelecionada || !serie) return null;
    return {
      colaboradorCodigo: linhaSelecionada.colaboradorCodigo,
      colaboradorNotaFiscalCodigo: parseColabNotaFiscalCodigo(linhaSelecionada.id),
      notaMaeEmpresaNotaFiscalCodigo: notaMae ? Number(notaMae.id) : null,
    };
  }, [linhaSelecionada, serie, notaMae]);

  const rcsQuery = useQuery({
    queryKey: ['nf-empresa-gerar', 'manual-rcs', companyId, listQuery],
    queryFn: () => listManualRequisicoesCompra(companyId, listQuery as ListManualRcsQuery),
    enabled: open && listQuery !== null,
    staleTime: 0,
  });

  const rows: RcRow[] = useMemo(
    () =>
      (rcsQuery.data ?? []).map((rc) => {
        const rowId = String(rc.requisicaoCompraCodigo) + '-' + rc.requisicaoCompraReferencia;
        const s = rowState[rowId] ?? { valor: 0, emissaoOK: false };
        return { ...rc, rowId, valor: s.valor, emissaoOK: s.emissaoOK };
      }),
    [rcsQuery.data, rowState],
  );

  function updateRow(rowId: string, patch: { valor?: number; emissaoOK?: boolean }): void {
    setRowState((prev) => ({
      ...prev,
      [rowId]: {
        valor: patch.valor ?? prev[rowId]?.valor ?? 0,
        emissaoOK: patch.emissaoOK ?? prev[rowId]?.emissaoOK ?? false,
      },
    }));
  }

  const saldoPendente = linhaSelecionada?.saldoPendente ?? 0;
  const linhasSelecionadas = rows.filter((r) => r.emissaoOK && r.valor > 0);
  const somaAlocada = linhasSelecionadas.reduce((s, r) => s + r.valor, 0);
  const excedeuSaldo = somaAlocada > saldoPendente + 0.005;
  const restante = saldoPendente - somaAlocada;

  const gerarMut = useMutation({
    mutationFn: () => {
      if (!listQuery || !linhaSelecionada) throw new Error('estado incompleto');
      return gerarManualBatch(companyId, {
        modelo,
        serie,
        colaboradorCodigo: linhaSelecionada.colaboradorCodigo,
        colaboradorNotaFiscalCodigo: parseColabNotaFiscalCodigo(linhaSelecionada.id),
        notaMaeEmpresaCodigo: notaMae ? Number(notaMae.id) : null,
        notaMaeEmpresaNotaFiscalCodigo: notaMae ? Number(notaMae.id) : null,
        requisicoes: linhasSelecionadas.map((r) => ({
          requisicaoCompraCodigo: r.requisicaoCompraCodigo,
          valor: r.valor,
        })),
      });
    },
    onSuccess: (r) => {
      toast.success(
        t('features.empresa.gerarNfEmpresa.manual.toasts.emitidas', { count: r.emitidas }),
      );
      onEmitido();
    },
    onError: (err) => toast.error(describeApiError(err)),
  });

  function handleClose(): void {
    setRowState({});
    onClose();
  }

  const columns: GridColDef<RcRow>[] = [
    {
      field: 'requisicaoCompraReferencia',
      headerName: t('features.empresa.gerarNfEmpresa.manual.columns.rc'),
      width: 180,
    },
    {
      field: 'requisicaoCompraSaldo',
      headerName: t('features.empresa.gerarNfEmpresa.manual.columns.saldo'),
      width: 150,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v: number) => formatBRL(v),
    },
    {
      field: 'requisicaoCompraProjetosVinculados',
      headerName: t('features.empresa.gerarNfEmpresa.manual.columns.projetos'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'valor',
      headerName: t('features.empresa.gerarNfEmpresa.manual.columns.valor'),
      width: 160,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <TextField
          size="small"
          type="number"
          value={params.row.valor === 0 ? '' : String(params.row.valor)}
          onChange={(e) => {
            const raw = e.target.value;
            const parsed = raw === '' ? 0 : Number.parseFloat(raw);
            const clamped = Number.isFinite(parsed)
              ? Math.max(0, Math.min(parsed, params.row.requisicaoCompraSaldo))
              : 0;
            updateRow(params.row.rowId, { valor: clamped });
          }}
          inputProps={{
            min: 0,
            max: params.row.requisicaoCompraSaldo,
            step: '0.01',
            style: { textAlign: 'right' },
          }}
          sx={{ width: '100%' }}
        />
      ),
    },
    {
      field: 'emissaoOK',
      headerName: t('features.empresa.gerarNfEmpresa.manual.columns.emissaoOk'),
      width: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Checkbox
          size="small"
          checked={params.row.emissaoOK}
          onChange={(e) => updateRow(params.row.rowId, { emissaoOK: e.target.checked })}
          disabled={params.row.valor <= 0}
        />
      ),
    },
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {t('features.empresa.gerarNfEmpresa.manual.title', {
          colaborador: linhaSelecionada?.colaboradorNome ?? '',
        })}
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
          <Chip
            label={`${t('features.empresa.gerarNfEmpresa.manual.saldoPendenteLabel')}: ${formatBRL(saldoPendente)}`}
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${t('features.empresa.gerarNfEmpresa.manual.somaAlocadaLabel')}: ${formatBRL(somaAlocada)}`}
            color={excedeuSaldo ? 'error' : 'success'}
          />
          {!excedeuSaldo && restante > 0.005 && (
            <Chip
              label={`${t('features.empresa.gerarNfEmpresa.manual.restanteLabel')}: ${formatBRL(restante)}`}
              variant="outlined"
            />
          )}
        </Stack>

        {rcsQuery.isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : rcsQuery.isError ? (
          <Alert severity="error">{describeApiError(rcsQuery.error)}</Alert>
        ) : rows.length === 0 ? (
          <Alert severity="info">{t('features.empresa.gerarNfEmpresa.manual.rcsVazio')}</Alert>
        ) : (
          <>
            <DataGrid<RcRow>
              autoHeight
              rows={rows}
              getRowId={(r) => r.rowId}
              columns={columns}
              disableRowSelectionOnClick
              disableColumnFilter
              disableColumnMenu
              disableVirtualization
              hideFooter
            />

            <Divider sx={{ my: 2 }} />

            <Alert severity="info" variant="outlined">
              {t('features.empresa.gerarNfEmpresa.manual.parcialHint')}
            </Alert>

            {excedeuSaldo && (
              <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
                {t('features.empresa.gerarNfEmpresa.manual.excedeuSaldo', {
                  saldo: formatBRL(saldoPendente),
                  soma: formatBRL(somaAlocada),
                })}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={gerarMut.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={
            gerarMut.isPending ||
            rcsQuery.isLoading ||
            linhasSelecionadas.length === 0 ||
            excedeuSaldo
          }
          onClick={() => gerarMut.mutate()}
        >
          {gerarMut.isPending
            ? t('common.saving')
            : t('features.empresa.gerarNfEmpresa.manual.confirmar', {
                count: linhasSelecionadas.length,
              })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
